// LinkResolver — relative-link + fragment resolution and image-src rewriting for
// rendered doc bodies (RFC-0010 §7, PD-0008 G20; and §10's image-proxy rewrite).
//
// Markdown bodies reference other docs and images with repo-relative paths. This
// module classifies every anchor `href` and image `src`:
//
//   anchor href:
//     - `#frag`                          → same-page fragment, passed through
//     - absolute http(s)                 → external passthrough (subject to §2's
//                                          sanitize guard, which already ran)
//     - absolute non-http(s) / `//host`  → blocked (null href)
//     - relative → resolved repo path:
//         · matches an indexed doc path  → in-app portal route
//                                          /projects/:repo/docs/<path>[#frag]
//         · no match                     → GitHub blob URL (never a 404)
//       the `#frag` suffix is preserved verbatim across either rewrite.
//
//   image src:
//     - absolute http(s)                 → external passthrough (unproxied — an
//                                          author-pasted external URL, RFC §10;
//                                          renders broken once img-src narrows)
//     - absolute non-http(s) / `//host`  → blocked (null)
//     - relative → the same-origin proxy /api/projects/:repo/raw?path=<resolved>
//       so the image loads under the narrowed `img-src 'self'` CSP.

import { isSafeHttpUrl } from '@/lib/url-scheme';

/** Repo identifiers needed to construct portal routes, blob URLs, and proxy URLs. */
export interface RepoLinkContext {
  /** Bare repo name — the `:repo` segment for portal routes and the image proxy. */
  repo: string;
  /** `owner/name` — for the GitHub blob URL fallback. */
  fullName: string;
  /** Default branch — the `blob/<branch>` segment of the GitHub blob URL. */
  defaultBranch: string;
}

export type ResolvedLink =
  | { kind: 'external'; href: string }
  | { kind: 'internal'; href: string }
  | { kind: 'blob'; href: string }
  | { kind: 'fragment'; href: string }
  | { kind: 'blocked'; href: null };

/** True when the value carries an explicit URL scheme (`scheme:`) or is `//host`. */
function hasScheme(value: string): boolean {
  return value.startsWith('//') || /^[a-z][a-z0-9+.-]*:/i.test(value);
}

/** Split a `path#fragment` into its path and (scheme-less) `#fragment` suffix. */
function splitFragment(value: string): { path: string; fragment: string } {
  const hash = value.indexOf('#');
  if (hash === -1) return { path: value, fragment: '' };
  return { path: value.slice(0, hash), fragment: value.slice(hash) };
}

/**
 * Resolve a repo-relative `to` against the directory of `fromDocPath`, collapsing
 * `.`/`..` segments. A leading `/` means repo-root-relative. Returns a posix
 * repo-relative path with no leading slash.
 */
export function resolveRepoPath(fromDocPath: string, to: string): string {
  let rel = to;
  let baseParts: string[];
  if (rel.startsWith('/')) {
    baseParts = [];
    rel = rel.replace(/^\/+/, '');
  } else {
    baseParts = fromDocPath.split('/').slice(0, -1);
  }
  const out = [...baseParts];
  for (const part of rel.split('/')) {
    if (part === '' || part === '.') continue;
    if (part === '..') {
      if (out.length) out.pop();
      continue;
    }
    out.push(part);
  }
  return out.join('/');
}

/**
 * Classify and rewrite an anchor `href` (RFC-0010 §7). `indexedPaths` is the set
 * of the repo's indexed doc paths (the non-null `docPath` nav leaves).
 */
export function resolveHref(
  href: string | null | undefined,
  currentDocPath: string,
  indexedPaths: ReadonlySet<string>,
  ctx: RepoLinkContext,
): ResolvedLink {
  if (typeof href !== 'string') return { kind: 'blocked', href: null };
  const trimmed = href.trim();
  if (trimmed === '') return { kind: 'blocked', href: null };

  // Pure same-page fragment.
  if (trimmed.startsWith('#')) return { kind: 'fragment', href: trimmed };

  // Absolute (has a scheme) — http(s) passes through, everything else is blocked.
  if (hasScheme(trimmed)) {
    return isSafeHttpUrl(trimmed)
      ? { kind: 'external', href: trimmed }
      : { kind: 'blocked', href: null };
  }

  // Relative — resolve against the current doc, then classify by index membership.
  const { path, fragment } = splitFragment(trimmed);
  const resolved = resolveRepoPath(currentDocPath, path);

  if (indexedPaths.has(resolved)) {
    return {
      kind: 'internal',
      href: `/projects/${encodeURIComponent(ctx.repo)}/docs/${resolved}${fragment}`,
    };
  }
  return {
    kind: 'blob',
    href: `https://github.com/${ctx.fullName}/blob/${ctx.defaultBranch}/${resolved}${fragment}`,
  };
}

/**
 * Rewrite an image `src` (RFC-0010 §10). Repo-relative images route through the
 * authenticated same-origin proxy; absolute http(s) images pass through
 * unproxied; other schemes are dropped (return `null`).
 */
export function resolveImageSrc(
  src: string | null | undefined,
  currentDocPath: string,
  ctx: RepoLinkContext,
): string | null {
  if (typeof src !== 'string') return null;
  const trimmed = src.trim();
  if (trimmed === '') return null;

  if (hasScheme(trimmed)) {
    // Absolute external image — pass http(s) through, block anything else.
    return isSafeHttpUrl(trimmed) ? trimmed : null;
  }

  const { path } = splitFragment(trimmed);
  const resolved = resolveRepoPath(currentDocPath, path);
  return `/api/projects/${encodeURIComponent(ctx.repo)}/raw?path=${encodeURIComponent(resolved)}`;
}
